import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { DEFAULT_PERIOD_OPTION } from "./consts";
import { PeriodOptionSchema, PeriodOptionType, Timespan } from "./types";
import { getTimespanFromPeriod } from "./utils";

export type TimespanPickerReturn = ReturnType<typeof useTimespanPicker>;
export const useTimespanPicker = () => {
  const [period, setPeriod] = useState<PeriodOptionType>(DEFAULT_PERIOD_OPTION);

  // Internal timespan is used when actively changing the timespan
  const [internalTimespan, setInternalTimespan] = useState<Timespan>(
    getTimespanFromPeriod(period)
  );

  // The external timespan is only updated when the popup is closed
  // and the internal timespan is valid
  const [timespan, setTimespan] = useState<Timespan>(
    getTimespanFromPeriod(period)
  );

  const minDate = useMemo(
    () => dayjs().subtract(18, "months").startOf("day").toDate(),
    []
  );

  const changePeriod = (value: string) => {
    const periodParse = PeriodOptionSchema.safeParse(value);
    if (!periodParse.success) {
      return;
    }
    const period = periodParse.data;
    setPeriod(period);
    setInternalTimespan(getTimespanFromPeriod(period));
  };

  const changeStartDate = (date: string | Date | null) => {
    // Cancel if not in custom period mode
    if (period !== "custom" || !date) {
      return;
    }

    // If date is null, set startDate to null
    if (date === null) {
      setInternalTimespan((prev) => ({
        ...prev,
        startDate: null,
      }));
      return;
    }

    // Cancel if date is before minDate or after current endDate
    if (
      dayjs(date).isBefore(minDate) ||
      dayjs(date).isAfter(internalTimespan.endDate)
    ) {
      return;
    }

    setInternalTimespan((prev) => ({
      ...prev,
      startDate: dayjs(date),
    }));
  };

  const changeEndDate = (date: string | Date | null) => {
    // Cancel if not in custom period mode
    if (period !== "custom") {
      return;
    }

    // If date is null, set endDate to null
    if (date === null) {
      setInternalTimespan((prev) => ({
        ...prev,
        endDate: null,
      }));
      return;
    }

    // Cancel if date is before minDate or before current startDate
    if (
      dayjs(date).isBefore(minDate) ||
      dayjs(date).isBefore(internalTimespan.startDate)
    ) {
      return;
    }

    setInternalTimespan((prev) => ({
      ...prev,
      endDate: dayjs(date),
    }));
  };

  // For updating both at once
  // Avoids an issue when changing startDate and endDate separately,
  // and new values are validated against old values.
  const changeTimespan = (
    startDate: string | Date | null,
    endDate: string | Date | null
  ) => {
    // Cancel if not in custom period mode
    if (period !== "custom") {
      return;
    }

    // If both are null, set both to null and return early
    if (startDate === null && endDate === null) {
      setInternalTimespan((prev) => ({
        ...prev,
        startDate: null,
        endDate: null,
      }));
      return;
    }

    const startDayJs = startDate ? dayjs(startDate) : null;
    const endDayJs = endDate ? dayjs(endDate) : null;

    // If startDate is given, validate it against minDate
    if (startDayJs !== null && startDayJs.isBefore(minDate)) {
      return;
    }

    // If endDate is given, validate it against minDate
    if (endDayJs !== null && endDayJs.isBefore(minDate)) {
      return;
    }

    // If both are given, validate them against each other
    if (
      startDayJs !== null &&
      endDayJs !== null &&
      startDayJs.isAfter(endDayJs)
    ) {
      return;
    }

    setInternalTimespan((prev) => ({
      ...prev,
      startDate: startDayJs,
      endDate: endDayJs,
    }));
  };

  const updateExternalTimespan = () => {
    if (
      internalTimespan.startDate === null ||
      internalTimespan.endDate === null
    ) {
      return;
    }
    setTimespan(internalTimespan);
  };

  return {
    period,
    changePeriod,
    internalTimespan,
    changeStartDate,
    changeEndDate,
    changeTimespan,
    minDate,
    updateExternalTimespan,
    timespan,
  };
};
